import React from "react";
import { useQuery } from "react-query";
import { isBillingEngineRequestError } from "services/BillingEngineService/interfaces";
import { Facility, fetchFacilityIndex } from "api/common/facility";

type FacilityByIdMap = Record<string, Facility>;

interface Props {
  facilities: Facility[];
  facilityById: FacilityByIdMap;
  isLoading: boolean;
}

const RateCardFacilitiesContext = React.createContext<Props | undefined>(undefined);

const RateCardFacilitiesProvider: React.FC = ({ children }) => {
  const { data, isLoading } = useQuery(["facilityIndex"], fetchFacilityIndex);
  const facilities: Facility[] = data?.filter((f) => !!f.enabled) || [];

  const facilityById = React.useMemo(() => {
    if (!isBillingEngineRequestError(facilities) && facilities) {
      return facilities.reduce((prev, facility) => {
        prev[facility.id] = facility;
        return prev;
      }, {} as FacilityByIdMap);
    }
    return {};
  }, [facilities]);

  return (
    <RateCardFacilitiesContext.Provider
      value={{
        facilities: facilities,
        facilityById: facilityById,
        isLoading: isLoading,
      }}
    >
      {children}
    </RateCardFacilitiesContext.Provider>
  );
};

/**
 * Hook to get access to OrganizationRateCardProvider
 */
const useRateCardFacilitiesContext = () => {
  const context = React.useContext(RateCardFacilitiesContext);

  if (!context) {
    throw new Error("useRateCardFacilitiesContext can be used only inside <RateCardFacilitiesProvider />");
  }

  return context;
};

export { RateCardFacilitiesProvider, useRateCardFacilitiesContext };
